/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Link } from '@tanstack/react-router'
import type { ComponentProps, CSSProperties, KeyboardEvent, MouseEventHandler, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type ContentCardLayout = 'compact' | 'expanded'

export interface ContentCardProps {
  /** Stacks image on top of the body (`expanded`) or side-by-side row (`compact`). */
  layout: ContentCardLayout
  /** In compact layout, image column on the start or end side. */
  imageSide?: 'left' | 'right'
  /** Cover media; omit to give the full main area to the body. */
  image?: ReactNode
  /** Optional overlay when `image` is set; children can use absolute positioning. */
  imageControls?: ReactNode
  /** Full-width strip above the image + body block. */
  header?: ReactNode
  /** Extra classes for the header strip wrapper. */
  headerClassName?: string
  /** Full-width strip below the image + body block. */
  footer?: ReactNode
  /** Extra classes for the footer strip wrapper. */
  footerClassName?: string
  /** Body when `layout="expanded"`; falls back to `children` if omitted. */
  bodyExpanded?: ReactNode
  /** Body when `layout="compact"`; falls back to `children` if omitted. */
  bodyCollapsed?: ReactNode
  /** Default body when the active layout's body prop is omitted. */
  children?: ReactNode
  /** Image column width when `layout="compact"`. CSS length (e.g. `'100px'`, `'30%'`) or number (px). */
  compactImageWidth?: string | number
  /** Image area height when `layout="expanded"`. CSS length or number (px). */
  expandedImageHeight?: string | number
  /** Minimum image column height when `layout="compact"`. CSS length or number (px). */
  compactImageMinHeight?: string | number
  /** Extra classes for the body wrapper. */
  bodyClassName?: string
  /** Click handler attached to the card root; enables `cursor-pointer` + hover lift. */
  onClick?: MouseEventHandler<HTMLDivElement>
  /**
   * TanStack Router destination. When set, the card root is a `<Link>` (with optional `params` / `search`).
   * `rootProps` apply only when the root is a `<div>`.
   */
  to?: ComponentProps<typeof Link>['to']
  /** Route params when `to` is set. */
  params?: ComponentProps<typeof Link>['params']
  /** URL/search params when `to` is set. */
  search?: ComponentProps<typeof Link>['search']
  /** Extra classes for the card root. */
  className?: string
  /** Props for the card root `<div>`. */
  rootProps?: Omit<ComponentProps<'div'>, 'children' | 'onClick' | 'className'>
  /** Props for the flex region that lays out image + body. */
  mainProps?: ComponentProps<'div'>
  /** Props for the image wrapper `<div>`. */
  imageAreaProps?: ComponentProps<'div'>
}

function toCssLength(value: string | number | undefined): string | undefined {
  if (value == null) {
    return undefined
  }
  return typeof value === 'number' ? `${value}px` : value
}

const cardBaseClasses =
  'group/content-card flex h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-card/60 hover:bg-card/20 text-card-foreground transition-[box-shadow,border-color] duration-200'

const cardClickableClasses =
  'cursor-pointer hover:border-foreground/50 hover:shadow-md focus-visible:outline-1 focus-visible:outline-primary focus-visible:outline-offset-2'

export function ContentCard({
  layout,
  imageSide = 'left',
  image,
  imageControls,
  header,
  headerClassName,
  footer,
  footerClassName,
  bodyExpanded,
  bodyCollapsed,
  children,
  compactImageWidth = '100px',
  expandedImageHeight = '150px',
  compactImageMinHeight = '150px',
  bodyClassName,
  onClick,
  to,
  params,
  search,
  className,
  rootProps,
  mainProps,
  imageAreaProps,
}: ContentCardProps) {
  const isExpanded = layout === 'expanded'
  const isImageRight = !isExpanded && imageSide === 'right'
  const hasImage = image != null && image !== false
  const bodyContent = isExpanded ? (bodyExpanded ?? children) : (bodyCollapsed ?? children)

  const isRouterLink = to != null
  const hasClickHandler = onClick != null
  const showAsClickable = hasClickHandler || isRouterLink

  const handleRootKeyDown =
    hasClickHandler && !isRouterLink
      ? (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onClick?.(event as unknown as Parameters<MouseEventHandler<HTMLDivElement>>[0])
          }
        }
      : undefined

  const { className: mainClassName, style: mainStyle, ...mainRest } = mainProps ?? {}
  const { className: imageAreaClassName, style: imageAreaStyle, ...imageAreaRest } = imageAreaProps ?? {}

  const compactImageWidthValue = toCssLength(compactImageWidth)
  const expandedImageHeightValue = toCssLength(expandedImageHeight)
  const compactImageMinHeightValue = toCssLength(compactImageMinHeight)

  const imageAreaInlineStyle: CSSProperties = isExpanded
    ? {
        width: '100%',
        height: expandedImageHeightValue,
        minHeight: expandedImageHeightValue,
      }
    : {
        width: compactImageWidthValue,
        minHeight: compactImageMinHeightValue,
      }

  const bodyInlineStyle: CSSProperties | undefined =
    hasImage && !isExpanded && compactImageMinHeightValue ? { minHeight: compactImageMinHeightValue } : undefined

  const rootClassNames = cn(
    cardBaseClasses,
    showAsClickable && cardClickableClasses,
    isRouterLink && 'w-full text-inherit no-underline',
    className
  )

  const inner = (
    <>
      {header != null ? (
        <div className={cn('w-full', headerClassName)} data-slot="content-card-header">
          {header}
        </div>
      ) : null}

      <div
        className={cn('flex min-w-0 flex-1 items-stretch', isExpanded ? 'flex-col' : 'flex-row', mainClassName)}
        data-slot="content-card-main"
        style={mainStyle}
        {...mainRest}
      >
        {hasImage ? (
          <div
            className={cn(
              'relative shrink-0 overflow-hidden transition-[width,height,min-width,min-height,flex-basis] duration-300 ease-in-out',
              isImageRight ? 'order-2' : 'order-0',
              imageAreaClassName
            )}
            data-slot="content-card-image"
            style={{ ...imageAreaInlineStyle, ...imageAreaStyle }}
            {...imageAreaRest}
          >
            <div className="relative h-full min-h-[inherit] w-full">
              {image}
              {imageControls != null ? (
                <div className="pointer-events-none absolute inset-0">
                  <div className="pointer-events-auto relative h-full w-full">{imageControls}</div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col p-2',
            isExpanded ? 'items-stretch justify-start' : 'items-start justify-center',
            hasImage ? 'order-1' : 'order-0 w-full',
            bodyClassName
          )}
          data-slot="content-card-body"
          style={bodyInlineStyle}
        >
          {bodyContent}
        </div>
      </div>

      {footer != null ? (
        <div className={cn('w-full p-2', footerClassName)} data-slot="content-card-footer">
          {footer}
        </div>
      ) : null}
    </>
  )

  if (isRouterLink) {
    return (
      <Link
        className={rootClassNames}
        data-slot="content-card"
        onClick={onClick as ComponentProps<typeof Link>['onClick']}
        params={params}
        search={search}
        to={to}
      >
        {inner}
      </Link>
    )
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Root may be clickable; we add role + keyboard handler when onClick is provided to keep it accessible.
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Root may be clickable; we add role + keyboard handler when onClick is provided to keep it accessible.
    <div
      className={rootClassNames}
      data-slot="content-card"
      onClick={onClick}
      onKeyDown={handleRootKeyDown}
      role={hasClickHandler ? 'button' : undefined}
      tabIndex={hasClickHandler ? 0 : undefined}
      {...rootProps}
    >
      {inner}
    </div>
  )
}
